<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary"> mdi-cog </v-icon>
        <span class="px-2">{{ $t('school_settings') }}</span>
      </v-card-title>
      <v-card-text>
        <vue-element-loading :active="isLoading" />
        <v-form v-if="settings">
          <div class="my-4">
            <label class="text--secondary font-weight-bold">{{ $t('school_code') }}</label>
          </div>
          <div class="my-4">
            <label class="text--secondary">{{settings.school_code}}</label>
          </div>
        </v-form>
        <v-form ref="form" v-if="settings" v-model="valid" lazy-validation class="my-2">
          <div class="my-4">
            <label class="text--secondary font-weight-bold">{{ $t('address_settings') }}</label>
          </div>
          <v-row>
            <v-col cols="12" md="4">
              <v-row>
                <v-col cols="12" md="3">
                  <label for="school-address">{{ $t('address') }}</label>
                </v-col>
                <v-col cols="12" md="9">
                  <div
                    class="
                      v-input v-input--hide-details v-input--dense
                      theme--light
                      v-text-field
                      v-text-field--is-booted
                      v-text-field--enclosed
                      v-text-field--outlined
                      v-text-field--placeholder
                    "
                    :class="{ 'v-input--has-state error--text': badAddress }"
                  >
                    <div class="v-input__control">
                      <div class="v-input__slot">
                        <div class="v-text-field__slot">
                          <GmapAutocomplete
                            id="school-address"
                            ref="schoolAddress"
                            @place_changed="setPlace"
                            :placeholder="$t('school_address')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-btn color="success" @click="useCurrentLocation" class="ma-1 ml-2 float-right my-2">
                  {{ $t('use_current_location') }}
                  <v-icon right dark> mdi-map-marker </v-icon>
                </v-btn>
              </v-row>
            </v-col>
            <v-col cols="12" md="8">
              <GoogleMapLoader
                :enabled="true"
                :center="center"
                :zoom="zoom"
                :apiKey="apiKey"
                :markers="markers"
                @map-click="handleMapClick"
              >
              </GoogleMapLoader>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{ $t('closed_week_days') }}</label>
                <info-tool-tip class="ml-2"
            :message="$t('set_closed_days_message')"></info-tool-tip>
          </div>
          <v-row>
            <v-col cols="12" md="2">
              <v-checkbox
                v-model="settings.saturday"
                :label="$t('saturday')"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="12" md="2">
              <v-checkbox
                v-model="settings.sunday"
                :label="$t('sunday')"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="12" md="2">
              <v-checkbox
                v-model="settings.monday"
                :label="$t('monday')"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="12" md="2">
              <v-checkbox
                v-model="settings.tuesday"
                :label="$t('tuesday')"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="12" md="2">
              <v-checkbox
                v-model="settings.wednesday"
                :label="$t('wednesday')"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="12" md="2">
              <v-checkbox
                v-model="settings.thursday"
                :label="$t('thursday')"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="12" md="2">
              <v-checkbox
                v-model="settings.friday"
                :label="$t('friday')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          
          <div class="my-4">
            <label class="text--secondary font-weight-bold">{{ $t('contact_info') }}</label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="settings.whatsapp_number"
                :label="$t('whatsapp_number')"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <div class="my-4">
            <label class="text--secondary font-weight-bold">{{ $t('description') }}</label>
          </div>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="settings.description"
                :label="$t('description')"
                outlined
                dense
                rows="3"
              ></v-textarea>
            </v-col>
          </v-row>

          <div class="my-4">
            <label class="text--secondary font-weight-bold">{{ $t('gallery') }}</label>
            <div class="text-caption text--secondary">
              {{ $t('gallery_max_hint', { max: maxGalleryImages }) }}
            </div>
          </div>
          <v-row>
            <v-col cols="12">
              <v-file-input
                v-model="newGallery"
                :disabled="remainingGallerySlots <= 0"
                :label="$t('upload_images')"
                multiple
                accept="image/*"
                prepend-icon="mdi-camera"
                outlined
                dense
                @change="onFileChange"
              ></v-file-input>
            </v-col>
          </v-row>
          
          <!-- Gallery Previews -->
          <v-row v-if="settings.gallery && settings.gallery.length > 0">
             <v-col cols="12">
               <label>{{ $t('current_images') }}</label>
             </v-col>
             <v-col v-for="(img, i) in settings.gallery" :key="'curr'+i" cols="6" md="3">
               <v-img :src="getImgUrl(img)" aspect-ratio="1.7" class="grey lighten-2">
                 <template v-slot:placeholder>
                   <v-row class="fill-height ma-0" align="center" justify="center">
                     <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                   </v-row>
                 </template>
                 <v-btn icon small color="red" class="float-right m-1" @click="removeCurrentInfo(i)">
                    <v-icon>mdi-close</v-icon>
                 </v-btn>
               </v-img>
             </v-col>
          </v-row>
          
           <!-- New Images Previews -->
          <v-row v-if="previewImages.length > 0">
             <v-col cols="12">
               <label>{{ $t('new_images') }}</label>
             </v-col>
             <v-col v-for="(img, i) in previewImages" :key="'new'+i" cols="6" md="3">
               <v-img :src="img" aspect-ratio="1.7" class="grey lighten-2"></v-img>
             </v-col>
          </v-row>
        </v-form>
      </v-card-text>
        <v-card-actions v-if="settings">
          <v-spacer></v-spacer>

          <v-btn :disabled="!valid" color="primary" @click="saveSettings">
            {{ $t('save') }}
            <v-icon right dark> mdi-content-save </v-icon>
          </v-btn>
        </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { loadGmapApi, gmapApi } from "vue2-google-maps";
import InfoToolTip from "@/components/InfoToolTip";
import VueElementLoading from "vue-element-loading";
import auth from '@/services/AuthService';
import GoogleMapLoader from "../../../components/GoogleMapLoader.vue";
import {Keys} from '/src/config.js'
export default {
  components: {
    VueElementLoading,
    InfoToolTip,
    GoogleMapLoader
  },
  data() {
    return {
        apiKey: Keys.GOOGLE_MAPS_API_KEY,
      isLoading: false,
      settings: null,
      valid: true,
      requiredRules: [(v) => !!v || this.$t("required")],
      badAddress: false,
      markers: [],
      currentPlace: null,
      center: {
        lat: Keys.VUE_APP_ORIGIN_LAT,
        lng: Keys.VUE_APP_ORIGIN_LNG,
      },
      zoom: 15,
      mode: null,
      newGallery: [],
      previewImages: [],
      submiting: false,
      maxGalleryImages: Keys.MAX_SCHOOL_GALLERY_IMAGES,
    };
  },
  computed: {
    google: gmapApi,
    remainingGallerySlots() {
      const existing = (this.settings && this.settings.gallery) ? this.settings.gallery.length : 0;
      const pending = this.newGallery ? this.newGallery.length : 0;
      return this.maxGalleryImages - existing - pending;
    },
  },
  mounted() {
    this.center.lat = parseFloat(this.center.lat);
    this.center.lng = parseFloat(this.center.lng);
    this.mode = auth.getMode();
    this.loadSettings();
  },
  methods: {
    useCurrentLocation() {
      this.isLoading = true;
      this.geolocate();
    },
    geolocate: function () {
        navigator.geolocation.getCurrentPosition((position) => {
            let geocoder = new google.maps.Geocoder();
            let latlng = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            geocoder
              .geocode({ location: latlng })
              .then((response) => {
                if (response.results[0]) {
                    this.setPlace(response.results[0]);
                    this.$nextTick(() => {
                      this.$refs.schoolAddress.$el.value = this.settings.address;
                    });
                } else {
                  window.alert(this.$t("no_results_found"));
                }
                this.isLoading = false;
              })
              .catch((e) => window.alert(this.$t("geocoder_failed") + e), this.isLoading = false);
        });
    },
    setPlace(place) {
      this.currentPlace = place;
      this.updateSchoolFromPlace(place);
      this.addSchoolMarker();
    },
    addSchoolMarker() {
      if (this.settings.lat && this.settings.lng) {
        const position = {
          lat: parseFloat(this.settings.lat),
          lng: parseFloat(this.settings.lng),
        };
        let marker = {
          place_id: this.settings.place_id,
          position: position,
        };
        this.markers = [];
        this.markers.push(marker);
        this.center = position;
      }
    },
    handleMapClick(place) {
      this.setPlace(place);
      this.$nextTick(() => {
        this.$refs.schoolAddress.$el.value = this.settings.address;
      });
    },
    updateSchoolFromPlace(place) {
      this.settings.place_id = place.place_id;
      this.settings.address = place.formatted_address;
      this.settings.lat = place.geometry.location.lat();
      this.settings.lng = place.geometry.location.lng();
    },
    onFileChange(files) {
        this.previewImages = [];
        if (!files || files.length === 0) {
          this.newGallery = [];
          return;
        }

        const existingCount = (this.settings && this.settings.gallery) ? this.settings.gallery.length : 0;
        const allowedCount = this.maxGalleryImages - existingCount;

        if (allowedCount <= 0) {
          this.newGallery = [];
          this.$notify({
            title: 'Error',
            text: this.$t('gallery_max_reached', { max: this.maxGalleryImages }),
            type: 'error',
          });
          return;
        }

        let selectedFiles = Array.from(files);
        if (selectedFiles.length > allowedCount) {
          selectedFiles = selectedFiles.slice(0, allowedCount);
          this.$notify({
            title: 'Warning',
            text: this.$t('gallery_max_partial', { max: this.maxGalleryImages }),
            type: 'warning',
          });
        }

        this.newGallery = selectedFiles;
        selectedFiles.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
                this.previewImages.push(e.target.result);
            }
            reader.readAsDataURL(file);
        });
    },
    removeCurrentInfo(index) {
        this.settings.gallery.splice(index, 1);
    },
    getImgUrl(path) {
        if(path.startsWith('http')) return path;
        // ensure leading slash
        if(!path.startsWith('/')) path = '/' + path;
        return path; 
    },
    //API Calls
    saveSettings() {
      const existingCount = (this.settings && this.settings.gallery) ? this.settings.gallery.length : 0;
      const newCount = this.newGallery ? this.newGallery.length : 0;
      if (existingCount + newCount > this.maxGalleryImages) {
        this.$notify({
          title: 'Error',
          text: this.$t('gallery_max_reached', { max: this.maxGalleryImages }),
          type: 'error',
        });
        return;
      }

      this.submiting = true;
      let formData = new FormData();
      
      formData.append('saturday', this.settings.saturday ? 1 : 0);
      formData.append('sunday', this.settings.sunday ? 1 : 0);
      formData.append('monday', this.settings.monday ? 1 : 0);
      formData.append('tuesday', this.settings.tuesday ? 1 : 0);
      formData.append('wednesday', this.settings.wednesday ? 1 : 0);
      formData.append('thursday', this.settings.thursday ? 1 : 0);
      formData.append('friday', this.settings.friday ? 1 : 0);
      
      formData.append('address', this.settings.address || '');
      formData.append('lat', this.settings.lat || '');
      formData.append('lng', this.settings.lng || '');
      formData.append('place_id', this.settings.place_id || '');
      formData.append('school_code', this.settings.school_code || ''); // Usually readonly but sending it back
      
      if(this.settings.whatsapp_number) formData.append('whatsapp_number', this.settings.whatsapp_number);
      if(this.settings.description) formData.append('description', this.settings.description);

      // Existing gallery items (strings)
      // The backend expects gallery[] for strings and files.
      if(this.settings.gallery && this.settings.gallery.length > 0) {
          this.settings.gallery.forEach(item => {
              formData.append('gallery[]', item);
          });
      }

      // New files
      if(this.newGallery && this.newGallery.length > 0) {
          this.newGallery.forEach(file => {
             formData.append('gallery[]', file); 
          });
      }

      axios
        .post("/settings/update-school", formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((response) => {
          this.submiting = false;
          this.newGallery = [];
          this.previewImages = [];
          // Reload settings to get new URLs
          this.loadSettings();

          this.$notify({
            title: "Success",
            text: this.$t("settings_updated"),
            type: "success",
          });
        })
        .catch((error) => {
          this.submiting = false;
          let message = this.$t("error_updating_settings");
          if(error.response && error.response.data && error.response.data.errors) {
              message = JSON.stringify(error.response.data.errors);
          }
           else if(error.response && error.response.data && error.response.data.message) {
              message = error.response.data.message;
          }

          this.$notify({
            title: "Error",
            text: message,
            type: "error",
          });
          console.log(error);
        });
    },
    loadSettings() {
      this.isLoading = true;
      this.settings = null;
      axios
        .get(`/settings/school`)
        .then((response) => {
          this.settings = response.data;
          if (!this.settings) {
              this.settings = {
                  saturday: 0, sunday: 0, monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0,
                  address: '', lat: '', lng: '', place_id: '', gallery: [],
                  whatsapp_number: '', description: '', school_code: ''
              };
          }
          this.$nextTick(() => {
              if (this.$refs.schoolAddress) {
                  this.$refs.schoolAddress.$el.value = this.settings.address || '';
              }
              this.addSchoolMarker();
          });
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: this.$t("error_retrieving_settings"),
            type: "error",
          });
          console.log(error);
          if (error.response && error.response.data) {
             auth.checkError(error.response.data.message, this.$router, this.$swal);
          }
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    validate() {
      this.valid = false;
      let v = this.$refs.form.validate();
      if (v) {
        this.valid = true;
        return true;
      }
      return false;
    },
  },
};
</script>
